<template>
  <div class="visual-mapping-container">
    <!-- Back to tree view when in drill-down mode -->
    <div v-if="current_view !== 'tree'" class="d-flex justify-content-end mb-3">
      <button 
        @click="reset_to_tree_view" 
        class="btn btn-bg-secondary text-xs btn-sm rounded-3"
      >
        ← Back to Tree View
      </button>
    </div>

    <!-- Tree View -->
    <div v-if="current_view === 'tree'">
      <div v-if="loading" class="text-center p-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <ApexTreeChart 
        v-if="treeData" 
        :data="treeData" 
        :entity-type="entityType"
        @node-click="handle_node_click"
      />
    </div>

    <!-- Customer Applications View -->
    <div v-else-if="current_view === 'applications' || current_view === 'workflow'">
      <CustomerApplications 
        :entity="entity"
        :entityType="entityType"
      />
    </div>

    <!-- Service Only View -->
    <div v-else-if="current_view === 'service'">
      <ServiceOnlyView
        :service_id="selected_service_id"
        :service_name="selected_service_name"
        :service_type="selected_service_type"
        :application_id="selected_application_id"
        :workflow_id="selected_workflow_id"
        :workflow_name="selected_workflow_name"
        :entity="entity"
      />
    </div>
  </div>
</template>

<script>
import ApexTreeChart from "@/components/TreeChart/ApexTreeChart.vue";
import CustomerApplications from "@/layouts/data_hub/components/customer_applications/CustomerApplications.vue";
import ServiceOnlyView from "@/layouts/data_hub/components/ServiceOnlyView.vue";
import { API_BASE_URL } from "@/config";

export default {
  name: "VisualMapping",
  components: {
    ApexTreeChart,
    CustomerApplications,
    ServiceOnlyView
  },
  props: {
    entity: {
      type: Object,
      required: true
    },
    entityType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      treeData: null,
      loading: false,
      current_view: 'tree',
      selected_workflow_id: null,
      selected_workflow_name: '',
      selected_workflow_description: '',
      selected_service_id: null,
      selected_service_name: '',
      selected_service_type: '',
      selected_application_id: null,
      node_metadata: {} // Store metadata for clicked nodes
    };
  },
  methods: {
    async fetchTreeData() {
      if (!this.entity || !this.entity.id) return;
      
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/verify_api/data_hub/entities/${this.entity.id}/tree/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          this.treeData = await response.json();
          this.extract_node_metadata(this.treeData);
        } else {
          console.error('Failed to fetch tree data:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching tree data:', error);
      } finally {
        this.loading = false;
      }
    },

    extract_node_metadata(node) {
      // Store metadata for each node to help with navigation
      if (node && node.id) {
        this.node_metadata[node.id] = {
          name: node.data?.name || '',
          type: node.data?.type || this.infer_node_type(node),
          parent_id: null,
          service_type: node.data?.service_type || '',
          application_id: node.data?.application_id || null,
          workflow_id: node.data?.workflow_id || null,
          workflow_description: node.data?.workflow_description || ''
        };
      }
      
      if (node && Array.isArray(node.children)) {
        node.children.forEach(child => {
          if (child && child.id) {
            this.node_metadata[child.id] = {
              name: child.data?.name || '',
              type: child.data?.type || this.infer_node_type(child),
              parent_id: node.id,
              service_type: child.data?.service_type || '',
              application_id: child.data?.application_id || null,
              workflow_id: child.data?.workflow_id || null,
              workflow_description: child.data?.workflow_description || ''
            };
          }
          this.extract_node_metadata(child);
        });
      }
    },

    infer_node_type(node) {
      const icon = node.data?.icon;
      if (icon === "bi-file-earmark-text") return "application";
      if (icon === "bi-diagram-3") return "workflow";  
      if (icon === "bi-gear") return "service";
      return "entity";
    },

    handle_node_click({ type, id }) {
      console.log('Node clicked:', { type, id });
      const metadata = this.node_metadata[id];
      
      if (type === 'application' || type === 'entity') {
        // Show customer applications view
        this.current_view = 'applications';
      } else if (type === 'workflow') {
        // Show workflow view with services
        this.selected_workflow_id = id;
        this.selected_workflow_name = metadata?.name || 'Workflow';
        this.selected_workflow_description = metadata?.workflow_description || '';
        this.current_view = 'workflow';
      } else if (type === 'service') {
        // Show service-only view using metadata directly from the tree
        this.selected_service_id = id;
        this.selected_service_name = metadata?.name || 'Service';
        this.selected_service_type = metadata?.service_type || '';
        this.selected_application_id = metadata?.application_id || null;
        this.selected_workflow_id = metadata?.workflow_id || null;
        
        // Get workflow name from the parent workflow if available
        if (this.selected_workflow_id && this.node_metadata[this.selected_workflow_id]) {
          this.selected_workflow_name = this.node_metadata[this.selected_workflow_id].name || 'Workflow';
        }
        
        this.current_view = 'service';
      }
    },

    handle_service_selected({ service, workflow_id, workflow_name, workflow_description, application_id }) {
      // Navigate to service-only view from workflow view
      this.selected_service_id = service.id;
      this.selected_service_name = service.service_name || service.name;
      this.selected_service_type = service.service_type || service.type;
      this.selected_workflow_id = workflow_id;
      this.selected_workflow_name = workflow_name;
      this.selected_workflow_description = workflow_description;
      this.selected_application_id = application_id;
      
      this.current_view = 'service';
    },

    reset_to_tree_view() {
      this.current_view = 'tree';
      this.clear_selections();
    },

    clear_selections() {
      this.selected_workflow_id = null;
      this.selected_workflow_name = '';
      this.selected_workflow_description = '';
      this.selected_service_id = null;
      this.selected_service_name = '';
      this.selected_service_type = '';
      this.selected_application_id = null;
    }
  },
  watch: {
    'entity.id': function() {
      this.reset_to_tree_view();
      this.fetchTreeData();
    }
  },
  mounted() {
    this.fetchTreeData();
  }
};
</script>

<style scoped>
.visual-mapping-container {
  padding: 20px;
}
</style>
